import React, { FC, ReactNode, useState, useCallback, useMemo } from "react";
import classNames from "classnames";
import makeStyles from "@material-ui/core/styles/makeStyles";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import CheckCircleOutlineOutlinedIcon from "@material-ui/icons/CheckCircleOutlineOutlined";
import theme from "styles/theme";
import { ValidationError } from "components/upload/validation-error";
import { DATATEST } from "constants/integration";
import { Theme } from "@material-ui/core/styles";

interface IProps {
    message?: ReactNode;
    children?: ReactNode;
    className?: string;
    level?: string;
    classes?: {
        root?: string;
        icon?: string;
        message?: string;
        content?: string;
    };
    onClose?(): void;
}

interface INotificationProps extends IProps {
    icon?: ReactNode;
    dataTest?: string;
}

export enum NotificationLevel {
    Error = "error",
    Info = "info",
    Warning = "warning",
    Success = "success",
}

const COLOR_SCHEMA = {
    [NotificationLevel.Error]: {
        borderColor: theme.palette.errorLightBorder,
        backgroundColor: theme.palette.errorLightBackground,
        iconColor: theme.palette.errorColor,
        textColor: "#e9635d",
    },
    [NotificationLevel.Warning]: {
        borderColor: theme.palette.warningAlertBorderColor,
        backgroundColor: theme.palette.warningAlertBackground,
        iconColor: theme.palette.warningAlertIconSignColor,
        textColor: theme.palette.warningAlertTextColor,
    },
    [NotificationLevel.Info]: {
        borderColor: theme.palette.infoAlertBorderColor,
        backgroundColor: theme.palette.infoAlertBackground,
        iconColor: theme.palette.infoAlertTextColor,
        textColor: theme.palette.infoAlertTextColor,
    },
    [NotificationLevel.Success]: {
        borderColor: theme.palette.successAlertBorderColor,
        backgroundColor: theme.palette.successAlertBackground,
        iconColor: theme.palette.successAlertTextColor,
        textColor: theme.palette.successAlertTextColor,
    },
};

export const NotificationInfo: FC<IProps> = ({ message, onClose, className, classes: propClasses }) => {
    const classes = useStyle(COLOR_SCHEMA[NotificationLevel.Info]);
    return !message ? null : (
        <NotificationTemplate
            dataTest={DATATEST.NOTIFICATION_BANNER.INFO}
            level={NotificationLevel.Info}
            message={message}
            onClose={onClose}
            classes={{
                root: classNames(classes.root, className, propClasses?.root),
                message: classNames(classes.message, classes.infoMessage, propClasses?.message),
                icon: classNames(classes.icon, propClasses?.icon),
            }}
        />
    );
};

export const NotificationWarning: FC<IProps> = ({ message, onClose, className, classes: propClasses }) => {
    const classes = useStyle(COLOR_SCHEMA[NotificationLevel.Warning]);
    return !message ? null : (
        <NotificationTemplate
            dataTest={DATATEST.NOTIFICATION_BANNER.WARNING}
            level={NotificationLevel.Warning}
            message={message}
            onClose={onClose}
            classes={{
                root: classNames(classes.root, className, propClasses?.root),
                message: classNames(classes.message, classes.infoMessage, propClasses?.message),
                icon: classNames(classes.icon, propClasses?.icon),
            }}
        />
    );
};

export const NotificationError: FC<IProps> = ({ message, onClose, className, classes: propClasses }) => {
    const classes = useStyle(COLOR_SCHEMA[NotificationLevel.Error]);
    return !message ? null : (
        <NotificationTemplate
            dataTest={DATATEST.NOTIFICATION_BANNER.ERROR}
            level={NotificationLevel.Error}
            message={message}
            onClose={onClose}
            classes={{
                root: classNames(classes.root, className, propClasses?.root),
                message: classNames(classes.message, propClasses?.message),
                icon: classNames(classes.icon, propClasses?.icon),
            }}
        />
    );
};

export const NotificationSuccess: FC<IProps> = ({ message, onClose, className, classes: propClasses }) => {
    const classes = useStyle(COLOR_SCHEMA[NotificationLevel.Success]);
    return !message ? null : (
        <NotificationTemplate
            dataTest={DATATEST.NOTIFICATION_BANNER.SUCCESS}
            level={NotificationLevel.Success}
            message={message}
            onClose={onClose}
            classes={{
                root: classNames(classes.root, className, propClasses?.root),
                message: classNames(classes.message, propClasses?.message),
                icon: classNames(classes.icon, propClasses?.icon),
            }}
        />
    );
};

export const NotificationTemplate: FC<INotificationProps> = (options) => {
    const { onClose, className, classes = {}, message, level = NotificationLevel.Info } = options;
    const { dataTest = DATATEST.NOTIFICATION_BANNER.ROOT, children } = options;
    const inner = useStyle({ ...COLOR_SCHEMA[level] });

    const icon = useMemo(() => {
        switch (level) {
            case NotificationLevel.Success:
                return <CheckCircleOutlineOutlinedIcon className={classNames(inner.icon, classes?.icon)} />;
            case NotificationLevel.Warning:
            case NotificationLevel.Error:
            case NotificationLevel.Info:
                return <ErrorOutlineIcon className={classNames(inner.icon, classes?.icon)} />;
            default:
                return options.icon;
        }
    }, [classes?.icon, inner.icon, level, options.icon]);

    return (
        <div data-test={dataTest} className={classNames(inner.root, className, classes.root)}>
            {icon}
            {message && <div className={classNames(inner.message, classes.message)}>{message}</div>}
            {children && <div className={classNames(inner.content, classes.content)}>{children}</div>}
            {onClose && (
                <IconButton aria-label="close" onClick={onClose}>
                    <CloseIcon className={classNames(classes.icon)} />
                </IconButton>
            )}
        </div>
    );
};

export const useNotificationState = (
    defaultMessage: string
): [string, (error?: { message: string } | Error) => void, () => void] => {
    const [state, setState] = useState<string>(null);
    const handler = useCallback(
        (error?: ValidationError) => setState(error?.message || defaultMessage),
        [defaultMessage]
    );
    const clear = useCallback(() => setState(null), []);
    return [state, handler, clear];
};

const useStyle = makeStyles<
    Theme,
    { borderColor: string; backgroundColor: string; iconColor: string; textColor: string }
>({
    root: ({ borderColor, backgroundColor }) => ({
        display: "flex",
        border: `1px solid ${borderColor}`,
        backgroundColor: `${backgroundColor}`,
        borderRadius: 4,
        alignItems: "center",
        padding: "2px 0 2px 10px",
        width: "100%",
        height: 48,
    }),
    content: {},
    message: ({ textColor }) => ({
        display: "flex",
        flexGrow: 1,
        fontSize: 12,
        padding: "0 15px",
        color: `${textColor}`,
    }),
    icon: ({ iconColor }) => ({
        color: `${iconColor}`,
        width: 24,
        height: 24,
    }),
    infoMessage: {
        "& p": {
            margin: 0,
        },
    },
});

export default NotificationError;
